/**
 * Schematic files → one common block grid.
 *
 * Reads the Java Edition formats people share builds in:
 *  - Litematica `.litematic` (all regions merged into their enclosing box)
 *  - Sponge / WorldEdit `.schem` (versions 1–3)
 *  - MCEdit / Schematica `.schematic` (pre-1.13 numeric ids, see legacy.ts)
 *  - vanilla structure files `.nbt` (structure blocks, jigsaw pieces)
 *
 * Everything is gzip-compressed NBT, so the world package's NBT reader does the heavy lifting.
 * Output index order is `(y * sizeZ + z) * sizeX + x`, palette index 0 is always air.
 */
import { parseNbtFile, LongArray, type NbtCompound, type NbtValue } from "./nbt.ts";
import { stateKey, paletteState } from "./anvil.ts";
import { LEGACY_NAME_IDS, legacyStates } from "./legacy.ts";

export type SchematicFormat = "litematic" | "sponge" | "mcedit" | "structure";

/** file extensions MC Planner can import (lower case, with dot) */
export const SCHEMATIC_EXTENSIONS = [".litematic", ".schem", ".schematic", ".nbt"];

export const SCHEMATIC_FORMAT_LABEL: Record<SchematicFormat, string> = {
  litematic: "Litematica",
  sponge: "WorldEdit (.schem)",
  mcedit: "MCEdit / Schematica (pre-1.13)",
  structure: "Structure block (.nbt)",
};

export interface Structure {
  format: SchematicFormat;
  /** version of the file format (Sponge 1–3, Litematica 4–7 …), 0 if unknown */
  version: number;
  /** Minecraft data version the file was saved with (null for old files) */
  dataVersion: number | null;
  /** from the file's metadata, if it has any */
  name: string | null;
  author: string | null;
  description: string | null;
  /** size in blocks: x (width), y (height), z (length) */
  size: [number, number, number];
  /** block-state keys ("minecraft:oak_stairs[facing=east,half=bottom,…]"); index 0 = air */
  palette: string[];
  /** palette index per block, index = (y * sizeZ + z) * sizeX + x */
  blocks: Uint16Array | Uint32Array;
  /** Litematica's preview picture (RGBA), if the file has one */
  preview: { w: number; h: number; rgba: Uint8Array } | null;
}

/** Largest schematic we load (blocks in the bounding box). */
export const MAX_SCHEMATIC_VOLUME = 64_000_000;

const AIR_NAMES = new Set(["minecraft:air", "minecraft:cave_air", "minecraft:void_air", "minecraft:structure_void"]);

const isCompound = (v: unknown): v is NbtCompound => !!v && typeof v === "object" && !Array.isArray(v) && !ArrayBuffer.isView(v) && !(v instanceof LongArray);
const num = (v: NbtValue | undefined): number => (typeof v === "number" ? v : typeof v === "bigint" ? Number(v) : 0);
const str = (v: NbtValue | undefined): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
/** WorldEdit writes sizes as (signed) shorts */
const u16 = (v: NbtValue | undefined) => num(v) & 0xffff;

/** "minecraft:oak_stairs[facing=east,half=bottom]" (an NBT "{…}" suffix is ignored) → name + properties */
export function parseStateKey(key: string): { name: string; props: Record<string, string> } {
  let k = key.trim();
  const brace = k.indexOf("{");
  if (brace >= 0) k = k.slice(0, brace);
  const open = k.indexOf("[");
  let name = (open >= 0 ? k.slice(0, open) : k).trim();
  if (name && !name.includes(":")) name = `minecraft:${name}`;
  const props: Record<string, string> = {};
  if (open >= 0) {
    const close = k.lastIndexOf("]");
    for (const kv of k.slice(open + 1, close > open ? close : undefined).split(",")) {
      const eq = kv.indexOf("=");
      if (eq > 0) props[kv.slice(0, eq).trim()] = kv.slice(eq + 1).trim();
    }
  }
  return { name, props };
}

class PaletteBuilder {
  readonly keys: string[] = ["minecraft:air"];
  private map = new Map<string, number>([["minecraft:air", 0]]);
  add(name: string, props?: Record<string, string>): number {
    if (!name || AIR_NAMES.has(name)) return 0;
    const key = stateKey(name, props);
    let i = this.map.get(key);
    if (i === undefined) {
      i = this.keys.length;
      if (i > 65535) throw new Error("This schematic uses more than 65 535 different block states.");
      this.keys.push(key);
      this.map.set(key, i);
    }
    return i;
  }
  /** add a key written as text (normalises the property order) */
  addKey(key: string): number {
    const { name, props } = parseStateKey(key);
    return this.add(name, props);
  }
}

function checkVolume(w: number, h: number, l: number) {
  if (w <= 0 || h <= 0 || l <= 0) throw new Error("This schematic is empty (size 0).");
  const v = w * h * l;
  if (v > MAX_SCHEMATIC_VOLUME) throw new Error(`This schematic is too big to preview (${w} × ${h} × ${l} = ${v.toLocaleString("en-US")} blocks; the limit is ${MAX_SCHEMATIC_VOLUME.toLocaleString("en-US")}).`);
}

// ---------------- Sponge / WorldEdit (.schem) ----------------

function parseSponge(root: NbtCompound): Structure {
  const sch = isCompound(root.Schematic) ? root.Schematic : root;
  const version = num(sch.Version) || 1;
  const [w, h, l] = [u16(sch.Width), u16(sch.Height), u16(sch.Length)];
  checkVolume(w, h, l);
  // v3 keeps blocks in a "Blocks" container, v1/v2 at the top level
  const container = isCompound(sch.Blocks) ? sch.Blocks : sch;
  const pal = container.Palette;
  const data = container === sch ? sch.BlockData : container.Data;
  const meta = isCompound(sch.Metadata) ? sch.Metadata : {};
  const pb = new PaletteBuilder();
  const remap: number[] = [];
  if (isCompound(pal)) for (const [key, idx] of Object.entries(pal)) remap[num(idx)] = pb.addKey(key);
  const blocks = new Uint16Array(w * h * l);
  if (data instanceof Int8Array && isCompound(pal)) {
    const bytes = new Uint8Array(data.buffer, data.byteOffset, data.length);
    let p = 0;
    for (let i = 0; i < blocks.length && p < bytes.length; i++) {
      // unsigned LEB128 varint
      let v = 0, shift = 0, b = 0;
      do { b = bytes[p++]; v |= (b & 0x7f) << shift; shift += 7; } while (b & 0x80 && shift < 35);
      blocks[i] = remap[v] ?? 0;
    }
  }
  return {
    format: "sponge", version, dataVersion: num(sch.DataVersion) || null,
    name: str(meta.Name), author: str(meta.Author), description: null,
    size: [w, h, l], palette: pb.keys, blocks, preview: null,
  };
}

// ---------------- Litematica (.litematic) ----------------

function parseLitematic(root: NbtCompound): Structure {
  const regions = root.Regions as NbtCompound;
  const meta = isCompound(root.Metadata) ? root.Metadata : {};
  interface Region { min: [number, number, number]; size: [number, number, number]; pal: NbtCompound[]; states: LongArray | null }
  const rs: Region[] = [];
  for (const r of Object.values(regions)) {
    if (!isCompound(r) || !isCompound(r.Position) || !isCompound(r.Size)) continue;
    const p = [num(r.Position.x), num(r.Position.y), num(r.Position.z)];
    const s = [num(r.Size.x), num(r.Size.y), num(r.Size.z)];
    if (!s[0] || !s[1] || !s[2]) continue;
    // negative sizes grow towards -x/-y/-z from the region's origin corner
    const min = p.map((v, i) => v + (s[i] < 0 ? s[i] + 1 : 0)) as [number, number, number];
    rs.push({ min, size: s.map(Math.abs) as [number, number, number], pal: Array.isArray(r.BlockStatePalette) ? (r.BlockStatePalette as NbtCompound[]) : [], states: r.BlockStates instanceof LongArray ? r.BlockStates : null });
  }
  if (!rs.length) throw new Error("This Litematica file has no regions with blocks.");
  const lo = [0, 1, 2].map((i) => Math.min(...rs.map((r) => r.min[i])));
  const hi = [0, 1, 2].map((i) => Math.max(...rs.map((r) => r.min[i] + r.size[i] - 1)));
  const [w, h, l] = [hi[0] - lo[0] + 1, hi[1] - lo[1] + 1, hi[2] - lo[2] + 1];
  checkVolume(w, h, l);
  const pb = new PaletteBuilder();
  const blocks = new Uint16Array(w * h * l);
  for (const r of rs) {
    const remap = r.pal.map((e) => { const p = paletteState(e); return pb.add(p.name, p.props); });
    if (!r.states) continue;
    // Litematica packs values tightly across long boundaries (unlike chunk sections since 1.16)
    const bits = Math.max(2, Math.ceil(Math.log2(Math.max(1, r.pal.length))));
    const mask = bits >= 31 ? 0x7fffffff : (1 << bits) - 1;
    const words = r.states.words; // [hi, lo] per long → 32-bit word k (LSB first) = words[k ^ 1]
    const nWords = words.length;
    const [sx, sy, sz] = r.size;
    const [ox, oy, oz] = [r.min[0] - lo[0], r.min[1] - lo[1], r.min[2] - lo[2]];
    let i = 0;
    for (let y = 0; y < sy; y++)
      for (let z = 0; z < sz; z++) {
        const row = ((y + oy) * l + (z + oz)) * w + ox;
        for (let x = 0; x < sx; x++, i++) {
          const bit = i * bits;
          const k = Math.floor(bit / 32);
          if (k >= nWords) continue;
          const off = bit - k * 32;
          let v = words[k ^ 1] >>> off;
          if (off + bits > 32 && k + 1 < nWords) v |= words[(k + 1) ^ 1] << (32 - off);
          const b = remap[v & mask] ?? 0;
          if (b) blocks[row + x] = b;
        }
      }
  }
  let preview: Structure["preview"] = null;
  const img = meta.PreviewImageData;
  if (img instanceof Int32Array && img.length) {
    const side = Math.round(Math.sqrt(img.length));
    if (side * side === img.length) {
      const rgba = new Uint8Array(img.length * 4);
      for (let j = 0; j < img.length; j++) {
        const c = img[j];
        rgba[j * 4] = (c >> 16) & 255; rgba[j * 4 + 1] = (c >> 8) & 255; rgba[j * 4 + 2] = c & 255; rgba[j * 4 + 3] = (c >>> 24) & 255;
      }
      preview = { w: side, h: side, rgba };
    }
  }
  return {
    format: "litematic", version: num(root.Version), dataVersion: num(root.MinecraftDataVersion) || null,
    name: str(meta.Name), author: str(meta.Author), description: str(meta.Description),
    size: [w, h, l], palette: pb.keys, blocks, preview,
  };
}

// ---------------- MCEdit / Schematica (.schematic) ----------------

function parseMcedit(root: NbtCompound): Structure {
  const [w, h, l] = [u16(root.Width), u16(root.Height), u16(root.Length)];
  checkVolume(w, h, l);
  const n = w * h * l;
  const b = root.Blocks as Int8Array;
  const d = root.Data instanceof Int8Array ? root.Data : null;
  const add = root.AddBlocks instanceof Int8Array ? root.AddBlocks : null;
  const ids = new Uint16Array(n);
  const data = new Uint8Array(n);
  for (let i = 0; i < n && i < b.length; i++) {
    let id = b[i] & 255;
    // 4 extra id bits per block (WorldEdit's order: even index = low nibble)
    if (add && i >> 1 < add.length) id |= i & 1 ? (add[i >> 1] & 0xf0) << 4 : (add[i >> 1] & 0x0f) << 8;
    ids[i] = id;
    data[i] = d ? d[i] & 15 : 0;
  }
  // Schematica stores which block name each id meant (needed for mods)
  let extra: Map<number, string> | undefined;
  if (isCompound(root.SchematicaMapping)) {
    const toLegacy = new Map<number, number>();
    extra = new Map();
    let next = 60000;
    for (const [name, v] of Object.entries(root.SchematicaMapping)) {
      const id = num(v);
      const short = name.startsWith("minecraft:") ? name.slice(10) : null;
      const legacy = short !== null ? LEGACY_NAME_IDS[short] : undefined;
      if (legacy !== undefined) toLegacy.set(id, legacy);
      else { toLegacy.set(id, next); extra.set(next, name.includes(":") ? name : `minecraft:${name}`); next++; }
    }
    for (let i = 0; i < n; i++) { const m = toLegacy.get(ids[i]); if (m !== undefined) ids[i] = m; }
  }
  const { palette, blocks } = legacyStates(ids, data, w, l, extra);
  return {
    format: "mcedit", version: 0, dataVersion: null,
    name: str(root.Name), author: str(root.Author), description: null,
    size: [w, h, l], palette, blocks, preview: null,
  };
}

// ---------------- vanilla structure files (.nbt) ----------------

function parseStructureNbt(root: NbtCompound): Structure {
  const size = root.size as NbtValue[];
  const [w, h, l] = [num(size[0]), num(size[1]), num(size[2])];
  checkVolume(w, h, l);
  let pal = Array.isArray(root.palette) ? (root.palette as NbtCompound[]) : null;
  // structures with random variants (shipwrecks …) have several palettes: show the first
  if (!pal && Array.isArray(root.palettes) && Array.isArray(root.palettes[0])) pal = root.palettes[0] as NbtCompound[];
  const pb = new PaletteBuilder();
  const jigsaw = new Set<number>();
  const remap = (pal ?? []).map((e, i) => {
    const p = paletteState(e); // {Name, Properties}, or 26.3's {id, properties} / bare strings
    if (p.name === "minecraft:jigsaw") jigsaw.add(i);
    return pb.add(p.name, p.props);
  });
  const blocks = new Uint16Array(w * h * l);
  for (const b of root.blocks as NbtCompound[]) {
    const pos = b.pos as NbtValue[] | undefined;
    if (!Array.isArray(pos)) continue;
    const [x, y, z] = [num(pos[0]), num(pos[1]), num(pos[2])];
    if (x < 0 || y < 0 || z < 0 || x >= w || y >= h || z >= l) continue;
    const st = num(b.state);
    let v = remap[st] ?? 0;
    // jigsaw blocks turn into their "final state" when the structure is placed
    if (jigsaw.has(st)) {
      const fs = isCompound(b.nbt) ? b.nbt.final_state : undefined;
      v = typeof fs === "string" ? pb.addKey(fs) : 0;
    }
    blocks[(y * l + z) * w + x] = v;
  }
  return {
    format: "structure", version: 0, dataVersion: num(root.DataVersion) || null,
    name: null, author: str(root.author), description: null,
    size: [w, h, l], palette: pb.keys, blocks, preview: null,
  };
}

// ---------------- entry point ----------------

/** Read a schematic file (any supported format). Throws an error with a readable message. */
export async function parseSchematic(bytes: Uint8Array, fileName = ""): Promise<Structure> {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".mcstructure")) throw new Error("Bedrock .mcstructure files aren't supported — please use a Java Edition schematic (.litematic, .schem, .schematic or .nbt).");
  if (lower.endsWith(".zip")) throw new Error("This is a .zip — unpack it first, then import the schematic file inside (.litematic, .schem, .schematic or .nbt).");
  let root: NbtCompound;
  try {
    root = await parseNbtFile(bytes);
  } catch {
    throw new Error(`“${fileName || "This file"}” isn't a schematic MC Planner can read (supported: .litematic, .schem, .schematic, .nbt).`);
  }
  if (isCompound(root.Regions)) return parseLitematic(root);
  const sp = isCompound(root.Schematic) ? root.Schematic : root;
  if (isCompound(sp.Palette) || isCompound(sp.Blocks) || sp.BlockData instanceof Int8Array) return parseSponge(root);
  if (root.Blocks instanceof Int8Array) return parseMcedit(root);
  if (Array.isArray(root.size) && Array.isArray(root.blocks)) return parseStructureNbt(root);
  if (isCompound(root.Schematic) && typeof sp.Width === "number") return parseSponge(root); // v3 without blocks
  throw new Error(`“${fileName || "This file"}” is NBT, but not a schematic format MC Planner knows (supported: .litematic, .schem, .schematic, .nbt).`);
}

// ---------------- helpers ----------------

/** Palette index at a block (0 = air, also outside the box). */
export function structureAt(s: Structure, x: number, y: number, z: number): number {
  const [w, h, l] = s.size;
  if (x < 0 || y < 0 || z < 0 || x >= w || y >= h || z >= l) return 0;
  return s.blocks[(y * l + z) * w + x];
}

/** How often each palette entry is used. */
export function paletteCounts(s: Structure): Uint32Array {
  const c = new Uint32Array(s.palette.length);
  const b = s.blocks;
  for (let i = 0; i < b.length; i++) c[b[i]]++;
  return c;
}

/** Non-air blocks. */
export function structureBlockCount(s: Structure, counts = paletteCounts(s)): number {
  let n = 0;
  for (let i = 1; i < counts.length; i++) n += counts[i];
  return n;
}

/** Blocks that are no item (technical blocks, the other half of doors/beds …) are left out. */
const NOT_ITEMS = new Set([
  "minecraft:air", "minecraft:cave_air", "minecraft:void_air", "minecraft:structure_void", "minecraft:piston_head", "minecraft:moving_piston",
  "minecraft:fire", "minecraft:soul_fire", "minecraft:nether_portal", "minecraft:end_portal", "minecraft:end_gateway", "minecraft:bubble_column",
  "minecraft:barrier", "minecraft:light", "minecraft:jigsaw", "minecraft:structure_block",
]);

/** Blocks that are placed with a different item. */
const ITEM_OF: Record<string, string> = {
  "minecraft:wall_torch": "minecraft:torch", "minecraft:soul_wall_torch": "minecraft:soul_torch", "minecraft:redstone_wall_torch": "minecraft:redstone_torch",
  "minecraft:copper_wall_torch": "minecraft:copper_torch", "minecraft:wheat": "minecraft:wheat_seeds", "minecraft:carrots": "minecraft:carrot",
  "minecraft:potatoes": "minecraft:potato", "minecraft:beetroots": "minecraft:beetroot_seeds", "minecraft:cocoa": "minecraft:cocoa_beans",
  "minecraft:sweet_berry_bush": "minecraft:sweet_berries", "minecraft:pumpkin_stem": "minecraft:pumpkin_seeds", "minecraft:attached_pumpkin_stem": "minecraft:pumpkin_seeds",
  "minecraft:melon_stem": "minecraft:melon_seeds", "minecraft:attached_melon_stem": "minecraft:melon_seeds", "minecraft:redstone_wire": "minecraft:redstone",
  "minecraft:tripwire": "minecraft:string", "minecraft:cave_vines": "minecraft:glow_berries", "minecraft:cave_vines_plant": "minecraft:glow_berries",
  "minecraft:kelp_plant": "minecraft:kelp", "minecraft:bamboo_sapling": "minecraft:bamboo", "minecraft:twisting_vines_plant": "minecraft:twisting_vines",
  "minecraft:weeping_vines_plant": "minecraft:weeping_vines", "minecraft:big_dripleaf_stem": "minecraft:big_dripleaf", "minecraft:tall_seagrass": "minecraft:seagrass",
  "minecraft:powder_snow": "minecraft:powder_snow_bucket", "minecraft:water_cauldron": "minecraft:cauldron", "minecraft:lava_cauldron": "minecraft:cauldron",
  "minecraft:powder_snow_cauldron": "minecraft:cauldron", "minecraft:torchflower_crop": "minecraft:torchflower_seeds", "minecraft:pitcher_crop": "minecraft:pitcher_pod",
  "minecraft:frosted_ice": "minecraft:ice",
};

/** The item(s) and how many you need for one block of this state. */
export function itemsForState(name: string, props: Record<string, string>): [string, number][] {
  if (NOT_ITEMS.has(name) || name.startsWith("legacy:")) return [];
  if (props.half === "upper") return []; // doors, tall flowers/grass: one item for both halves
  if (props.part === "head" && name.endsWith("_bed")) return [];
  if (name === "minecraft:water" || name === "minecraft:lava") return !props.level || props.level === "0" ? [[`${name}_bucket`, 1]] : [];
  if (name.startsWith("minecraft:potted_")) {
    const plant = name.slice("minecraft:potted_".length);
    return [["minecraft:flower_pot", 1], [plant === "azalea_bush" ? "minecraft:azalea" : plant === "flowering_azalea_bush" ? "minecraft:flowering_azalea" : `minecraft:${plant}`, 1]];
  }
  if (props.type === "double" && name.endsWith("_slab")) return [[name, 2]];
  let n = 1;
  for (const k of ["candles", "pickles", "eggs", "flower_amount", "segment_amount"]) if (props[k]) n = Math.max(1, +props[k] || 1);
  if (name === "minecraft:snow" && props.layers) n = Math.max(1, +props.layers || 1);
  const item = ITEM_OF[name] ?? name.replace(/_wall_(sign|hanging_sign|banner|head|skull|fan)$/, "_$1");
  return [[item, n]];
}

/**
 * What you need to build it: item id → count, largest first.
 * (Double slabs count twice, one door/bed per two blocks, water/lava sources as buckets …)
 */
export function materialList(s: Structure, counts = paletteCounts(s)): [string, number][] {
  const m = new Map<string, number>();
  for (let i = 1; i < s.palette.length; i++) {
    if (!counts[i]) continue;
    const { name, props } = parseStateKey(s.palette[i]);
    for (const [item, k] of itemsForState(name, props)) m.set(item, (m.get(item) ?? 0) + k * counts[i]);
  }
  return [...m].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/**
 * Blocks renamed between Minecraft versions (both directions). A schematic saved with another
 * version still shows the right model: the name the loaded game jar knows wins.
 */
const RENAMES: [string, string][] = [
  ["minecraft:grass", "minecraft:short_grass"],
  ["minecraft:grass_path", "minecraft:dirt_path"],
  ["minecraft:chain", "minecraft:iron_chain"],
  ["minecraft:sign", "minecraft:oak_sign"],
  ["minecraft:wall_sign", "minecraft:oak_wall_sign"],
];
const RENAME = new Map<string, string>([...RENAMES, ...RENAMES.map(([a, b]) => [b, a] as [string, string])]);

/** `name` if the game knows it, else its old/new name if the game knows that one. */
export function resolveBlockName(name: string, known: (name: string) => boolean): string {
  if (known(name)) return name;
  const alt = RENAME.get(name);
  return alt && known(alt) ? alt : name;
}
